/**
 * Integrations module — central place to connect data sources to C-ORE.
 *
 *  • 6-card grid for Tally / Zoho / GST / Odoo / Razorpay / Excel.
 *  • Each card shows status, last sync, frequency and quick actions.
 *  • Click a card → side drawer with credentials form, setup steps,
 *    sync history and frequency settings.
 *  • Excel card opens the file-upload + column-mapping drawer.
 *  • A unified Confidence chip at the top mirrors the rule in
 *    `confidence_engine.py` (0-30 manual / 31-60 / 61-80 / 81-100).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, AlertOctagon, RefreshCw, Settings2, Plug, X, Loader2,
  Upload, ChevronRight, FileSpreadsheet, ShieldCheck, Activity, Sparkles, Send,
} from "lucide-react";
import { getCurrentCustomer } from "../lib/customerSession";
import IntegrationDrawer from "../components/integrations/IntegrationDrawer";
import { BACKEND_URL } from "../lib/backendUrl";

const API = `${BACKEND_URL}/api/integrations`;

const PROVIDER_TINT = {
  tally:      "from-amber-50 to-white",
  zoho:       "from-rose-50 to-white",
  gst:        "from-blue-50 to-white",
  odoo:       "from-violet-50 to-white",
  razorpay:   "from-indigo-50 to-white",
  quickbooks: "from-teal-50 to-white",
  xero:       "from-cyan-50 to-white",
  erpnext:    "from-sky-50 to-white",
  sap_b1:     "from-blue-50 to-white",
  netsuite:   "from-orange-50 to-white",
  excel:      "from-emerald-50 to-white",
};

const PROVIDER_INITIAL = {
  tally: "T", zoho: "Z", gst: "G", odoo: "O", razorpay: "R",
  quickbooks: "Q", xero: "X", erpnext: "E", sap_b1: "S", netsuite: "N", excel: "F",
};

const SECTION_DEFS = [
  { tier: "native",       title: "Native Integrations", subtitle: "Real, production-grade connections — recommended" },
  { tier: "manual",       title: "Manual Import",        subtitle: "No live connection needed — upload a statement any time" },
  { tier: "early_access", title: "Early Access",         subtitle: "Available through assisted onboarding with the C-ORE Solutions Team" },
  { tier: "coming_soon",  title: "Coming Soon",          subtitle: "On the roadmap — not connectable yet, shown for transparency" },
];

function timeAgo(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "Never";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)      return "Just now";
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400*7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function StatusPill({ status }) {
  const map = {
    connected:    { label: "Connected",    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: CheckCircle2 },
    disconnected: { label: "Not connected", cls: "bg-slate-100 text-slate-600 ring-slate-200",      Icon: Plug },
    pending:      { label: "Marked — add creds", cls: "bg-amber-50 text-amber-800 ring-amber-200", Icon: AlertOctagon },
    error:        { label: "Error",        cls: "bg-rose-50 text-rose-700 ring-rose-200",         Icon: AlertOctagon },
    partial:      { label: "Partial",      cls: "bg-amber-50 text-amber-800 ring-amber-200",      Icon: AlertOctagon },
    syncing:      { label: "Syncing…",     cls: "bg-blue-50 text-blue-700 ring-blue-200",         Icon: RefreshCw },
  };
  const m = map[status] || map.disconnected;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ${m.cls}`}>
      <Icon size={11} className={status === "syncing" ? "animate-spin" : ""} />
      {m.label}
    </span>
  );
}

function ConfidenceChip({ confidence }) {
  if (!confidence) return null;
  const pct = confidence.percent || 0;
  const total = confidence.total_connectable || 3;
  const tone =
    pct >= 81 ? "bg-emerald-50 text-emerald-700 ring-emerald-200" :
    pct >= 61 ? "bg-blue-50 text-blue-700 ring-blue-200" :
    pct >= 31 ? "bg-amber-50 text-amber-800 ring-amber-200" :
                "bg-slate-100 text-slate-600 ring-slate-200";
  return (
    <span data-testid="integrations-confidence"
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${tone}`}
          title={(confidence.sources || []).join(", ") || "No genuine sources connected yet"}>
      <ShieldCheck size={12} />
      Data confidence · {pct}%
      <span className="ml-1 font-normal opacity-70">
        {confidence.connected_count}/{total} connected
      </span>
    </span>
  );
}

function ProviderCard({ row, onOpen, onResync, resyncing }) {
  const def = row.definition || {};
  const tint = PROVIDER_TINT[def.id] || "from-slate-50 to-white";
  const initial = PROVIDER_INITIAL[def.id] || "?";
  const isAgentPush = def.sync_mode === "agent_push"; // Tally — the desktop agent pushes on its own schedule, no on-demand resync exists

  return (
    <div
      data-testid={`integration-card-${def.id}`}
      className={`group flex flex-col rounded-xl border border-slate-200 bg-gradient-to-br ${tint} p-4 shadow-sm transition-shadow hover:shadow-md`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0F1B2D] text-[15px] font-bold text-white">
            {initial}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <div className="text-[14px] font-bold text-[#0F1B2D]">{def.name}</div>
              {def.recommended && (
                <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                  Recommended
                </span>
              )}
            </div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{def.category}</div>
          </div>
        </div>
        <StatusPill status={row.status} />
      </div>

      <p className="mt-3 text-[12.5px] leading-relaxed text-slate-600 line-clamp-2">{def.blurb}</p>

      <div className="mt-4 grid grid-cols-3 gap-2 text-[10.5px]">
        <div className="rounded-md bg-white/70 px-2 py-1.5 ring-1 ring-slate-200">
          <div className="uppercase tracking-wider text-slate-500">Last sync</div>
          <div className="mt-0.5 font-semibold text-[#0F1B2D] tabular-nums">{timeAgo(row.last_sync_at)}</div>
        </div>
        <div className="rounded-md bg-white/70 px-2 py-1.5 ring-1 ring-slate-200">
          <div className="uppercase tracking-wider text-slate-500">Frequency</div>
          <div className="mt-0.5 font-semibold text-[#0F1B2D] tabular-nums">
            {isAgentPush ? "Agent-driven" : row.sync_frequency_hours ? `${row.sync_frequency_hours}h` : "On demand"}
          </div>
        </div>
        <div className="rounded-md bg-white/70 px-2 py-1.5 ring-1 ring-slate-200">
          <div className="uppercase tracking-wider text-slate-500">Records</div>
          <div className="mt-0.5 font-semibold text-[#0F1B2D] tabular-nums">
            {(row.records_synced || 0).toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      {row.last_error && (
        <div className="mt-2 truncate rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700 ring-1 ring-rose-200">
          {row.last_error}
        </div>
      )}

      {isAgentPush && row.status === "connected" && (
        <div data-testid={`integration-agentpush-note-${def.id}`} className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700">
          <Activity size={11} /> Auto-syncs from your Tally Agent every {row.sync_frequency_hours || 6}h — no manual resync needed.
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 pt-1">
        <button
          data-testid={`integration-open-${def.id}`}
          onClick={() => onOpen(def.id)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#0F1B2D] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#1E3A5F]"
        >
          {row.status === "connected" ? <>Manage <ChevronRight size={12} /></>
            : def.id === "excel"      ? <><Upload size={12} /> Upload</>
                                      : <><Plug size={12} /> Connect</>}
        </button>
        {row.status === "connected" && def.id !== "excel" && !isAgentPush && (
          <button
            data-testid={`integration-resync-${def.id}`}
            onClick={() => onResync(def.id)}
            disabled={resyncing}
            title="Resync now"
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white p-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            {resyncing
              ? <Loader2 size={13} className="animate-spin text-slate-500" />
              : <RefreshCw size={13} className="text-slate-600" />}
          </button>
        )}
      </div>
    </div>
  );
}

function EarlyAccessCard({ def, row, onRequest, requesting, confirmationMessage }) {
  const tint = PROVIDER_TINT[def.id] || "from-slate-50 to-white";
  const initial = PROVIDER_INITIAL[def.id] || "?";
  const alreadyRequested = !!row?.access_requested;
  const showConfirmation = !!confirmationMessage; // shown once, right after a click this session
  return (
    <div
      data-testid={`integration-card-${def.id}`}
      className={`flex flex-col rounded-xl border border-blue-200 bg-gradient-to-br ${tint} p-4`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#1E3A5F] text-[15px] font-bold text-white">
            {initial}
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#0F1B2D]">{def.name}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{def.category}</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10.5px] font-semibold text-blue-700 ring-1 ring-blue-200">
          <Sparkles size={10} /> Request Access
        </span>
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-slate-600 line-clamp-2">{def.blurb}</p>
      <p className="mt-2 text-[11px] text-slate-500">
        Available through assisted onboarding — the C-ORE Solutions Team sets this up with you.
      </p>

      {showConfirmation ? (
        <div data-testid={`integration-request-confirmation-${def.id}`}
             className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-[11.5px] leading-relaxed text-emerald-700 ring-1 ring-emerald-200">
          {confirmationMessage}
        </div>
      ) : (
        <button
          data-testid={`integration-request-${def.id}`}
          onClick={() => onRequest(def.id)}
          disabled={requesting || alreadyRequested}
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#1E3A5F] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#0F1B2D] disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {requesting
            ? <><Loader2 size={12} className="animate-spin" /> Requesting…</>
            : alreadyRequested
              ? <><CheckCircle2 size={12} /> Request Sent</>
              : <><Send size={12} /> Request</>}
        </button>
      )}
    </div>
  );
}

function ComingSoonCard({ def }) {
  const tint = PROVIDER_TINT[def.id] || "from-slate-50 to-white";
  const initial = PROVIDER_INITIAL[def.id] || "?";
  return (
    <div
      data-testid={`integration-card-${def.id}`}
      className={`flex flex-col rounded-xl border border-dashed border-slate-200 bg-gradient-to-br ${tint} p-4 opacity-70`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-400 text-[15px] font-bold text-white">
            {initial}
          </div>
          <div>
            <div className="text-[14px] font-bold text-slate-700">{def.name}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400">{def.category}</div>
          </div>
        </div>
        <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600">
          Coming Soon
        </span>
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-slate-500 line-clamp-2">{def.blurb}</p>
      <button
        data-testid={`integration-open-${def.id}`}
        disabled
        title="Not yet available"
        className="mt-3 inline-flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white/60 px-3 py-1.5 text-[12px] font-semibold text-slate-400"
      >
        Not Yet Available
      </button>
    </div>
  );
}

export default function IntegrationsPage() {
  const customer = useMemo(() => {
    try { return getCurrentCustomer(); } catch (_e) { return null; }
  }, []);
  const customerId = customer?.id;

  const [rows, setRows] = useState([]);
  const [confidence, setConfidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openProvider, setOpenProvider] = useState(null);
  const [resyncing, setResyncing] = useState({});
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true); setError(null);
    try {
      if (!customerId) {
        // Visitor mode — fetch provider definitions only so the grid still renders.
        const r = await fetch(`${API}/providers`); const j = await r.json();
        const stub = (j.providers || []).map((p) => ({
          provider: p.id, status: "disconnected", definition: p,
          last_sync_at: null, sync_frequency_hours: p.default_frequency_hours, records_synced: 0,
        }));
        setRows(stub); setConfidence({ percent: 0, band: "manual-only", connected_count: 0 });
      } else {
        const r = await fetch(`${API}/list`, { credentials: "include" });
        const j = await r.json();
        setRows(j.integrations || []);
        setConfidence(j.confidence || null);
      }
    } catch (e) {
      setError(e.message || "Could not load integrations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const handleResync = async (providerId) => {
    if (!customerId) return;
    setResyncing((p) => ({ ...p, [providerId]: true }));
    try {
      const r = await fetch(`${API}/sync`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: providerId }),
      });
      const j = await r.json();
      // Tell every open dashboard to refresh instantly.
      try {
        window.dispatchEvent(new CustomEvent("core:pipeline-done", {
          detail: { provider: providerId, pipeline: j?.pipeline || null,
                    records_synced: j?.records_synced || 0 },
        }));
      } catch (_e) { /* no-op */ }
      await load();
    } catch (_e) { /* swallow — error already surfaced via the row's last_error */ }
    finally { setResyncing((p) => ({ ...p, [providerId]: false })); }
  };

  const [requesting, setRequesting] = useState({});
  const [requestMessage, setRequestMessage] = useState({}); // { [providerId]: confirmation text }

  const handleRequestAccess = async (providerId) => {
    if (!customerId) return;
    setRequesting((p) => ({ ...p, [providerId]: true }));
    try {
      const r = await fetch(`${API}/request-access`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: providerId }),
      });
      const j = await r.json();
      if (r.ok && j.message) {
        setRequestMessage((p) => ({ ...p, [providerId]: j.message }));
      }
      await load();
    } catch (_e) { /* swallow — button state just won't advance */ }
    finally { setRequesting((p) => ({ ...p, [providerId]: false })); }
  };

  return (
    <div data-testid="integrations-module" className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700">
            <Activity size={13} /> C-ORE Integrations
          </div>
          <h1 className="mt-1 text-[22px] font-bold text-[#0F1B2D]">
            Connect your data — get a complete view
          </h1>
          <p className="mt-1 text-[12.5px] text-slate-500">
            The more sources you connect, the higher the confidence on every insight. C-ORE pipelines run in 6 steps after every sync.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ConfidenceChip confidence={confidence} />
          <button
            data-testid="integrations-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {!customerId && (
        <div data-testid="integrations-visitor-banner" className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-[12.5px] text-blue-900">
          You're exploring the demo. <span className="font-semibold">Sign up</span> to connect your own integrations — your dashboards will switch to live data instantly.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{error}</div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div data-testid="integrations-grid" className="space-y-7">
          {SECTION_DEFS.map(({ tier, title, subtitle }) => {
            const sectionRows = rows.filter((r) => (r.definition?.tier || "coming_soon") === tier);
            if (sectionRows.length === 0) return null;
            return (
              <div key={tier} data-testid={`integrations-section-${tier}`}>
                <div className="mb-3">
                  <h2 className="text-[13.5px] font-bold uppercase tracking-wide text-[#0F1B2D]">{title}</h2>
                  <p className="text-[11.5px] text-slate-500">{subtitle}</p>
                </div>
                <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
                  {sectionRows.map((r) => (
                    tier === "coming_soon" ? (
                      <ComingSoonCard key={r.provider} def={r.definition} />
                    ) : tier === "early_access" ? (
                      <EarlyAccessCard
                        key={r.provider}
                        def={r.definition}
                        row={r}
                        onRequest={handleRequestAccess}
                        requesting={!!requesting[r.provider]}
                        confirmationMessage={requestMessage[r.provider]}
                      />
                    ) : (
                      <ProviderCard
                        key={r.provider}
                        row={r}
                        onOpen={(id) => {
                          if (id === "excel") {
                            navigate("/financial-sync");
                          } else {
                            setOpenProvider(id);
                          }
                        }}
                        onResync={handleResync}
                        resyncing={!!resyncing[r.provider]}
                      />
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drawers */}
      {openProvider && openProvider !== "excel" && (
        <IntegrationDrawer
          providerId={openProvider}
          customerId={customerId}
          row={rows.find((r) => r.provider === openProvider)}
          onClose={() => setOpenProvider(null)}
          onChanged={load}
        />
      )}
      {/* Note: Excel card now navigates to /financial-sync instead of
          opening the legacy ExcelUploadDrawer modal. The drawer
          component is retired when Phase 2 absorbs the operational
          upload path. */}
    </div>
  );
}
